import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Link, Navigate } from 'react-router-dom';
import { Stethoscope, ArrowLeft, Plus, Trash2, Eye, EyeOff, Loader2, X, Upload, AlertTriangle, FileDown, Link2, Copy, Clock, Gift } from 'lucide-react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const EMPTY = {
  name: '', credentials: '', photo: '', bio: '', modality: 'telehealth',
  specialties: [], states: [], insurance: [], languages: [],
  booking_url: '', email: '', phone: '',
  faith_informed: false, trauma_informed: false, published: true,
};

const asArr = (a) => (Array.isArray(a) ? a : (a ? String(a).split(',').map((x) => x.trim()).filter(Boolean) : []));

// CSV template for bulk import. Multi-value cells use ';' (e.g. "Anxiety;Depression").
const CSV_TEMPLATE = [
  'name,credentials,state,specialties,insurance,languages,modality,telehealth,gender,race_ethnicity,faith_informed,trauma_informed,bio,website,email,phone',
  'Jane Doe,LCSW,CA,Anxiety;Depression,Aetna;Cigna,English;Spanish,telehealth,true,Female,Black,false,true,Compassionate licensed therapist.,https://example.com,jane@example.com,555-123-4567',
].join('\n') + '\n';

// Chip multi-select: pick from suggestions or type a new value + Enter.
function TagInput({ label, values, onChange, suggestions = [], testid, fixed }) {
  const [input, setInput] = useState('');
  const add = (v) => {
    const val = v.trim();
    if (!val || values.includes(val)) return;
    onChange([...values, val]); setInput('');
  };
  const remove = (v) => onChange(values.filter((x) => x !== v));
  const available = suggestions.filter((s) => !values.includes(s) && s.toLowerCase().includes(input.toLowerCase())).slice(0, 12);
  return (
    <div data-testid={testid}>
      <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">{label}</label>
      <div className="mt-1 flex flex-wrap gap-1.5 bg-[#F9F8F6] border border-navy/10 rounded-xl p-2 min-h-[42px]">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 bg-navy text-white rounded-full pl-2.5 pr-1 py-1 text-xs" data-testid={`${testid}-chip-${v}`}>
            {v}<button type="button" onClick={() => remove(v)} className="hover:bg-white/20 rounded-full p-0.5"><X size={11}/></button>
          </span>
        ))}
        {!fixed && (
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
            placeholder={values.length ? 'Add…' : 'Type + Enter, or pick below'}
            data-testid={`${testid}-input`}
            className="flex-1 min-w-[90px] bg-transparent text-sm outline-none px-1"/>
        )}
      </div>
      {(available.length > 0 || (fixed && input)) && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {(fixed ? suggestions.filter((s) => !values.includes(s)).slice(0, 60) : available).map((s) => (
            <button type="button" key={s} onClick={() => add(s)} data-testid={`${testid}-opt-${s}`}
              className="text-[11px] bg-beige text-navy/70 hover:text-navy rounded-full px-2.5 py-1">+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, ph, area, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">{label}</label>
      {area ? (
        <textarea value={v} onChange={(e) => on(e.target.value)} rows={3} placeholder={ph} data-testid={testid}
          className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gold"/>
      ) : (
        <input value={v} onChange={(e) => on(e.target.value)} placeholder={ph} data-testid={testid}
          className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"/>
      )}
    </div>
  );
}

export default function AdminTherapists() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [taxonomy, setTaxonomy] = useState({ specialties: [], insurance: [], languages: [] });
  const [showReview, setShowReview] = useState(false);
  const [showInvites, setShowInvites] = useState(false);
  const [invites, setInvites] = useState([]);
  const [invName, setInvName] = useState('');
  const [invEmail, setInvEmail] = useState('');
  const [invDays, setInvDays] = useState(14);
  const [invSendEmail, setInvSendEmail] = useState(true);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [editReview, setEditReview] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [trialStats, setTrialStats] = useState(null);
  const [trialBusy, setTrialBusy] = useState(null);
  const fileRef = useRef(null);

  const publicBase = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, '');
  const loadInvites = useCallback(async () => {
    try { const r = await http.get('/admin/therapists/invites'); setInvites(Array.isArray(r.data) ? r.data : []); }
    catch { /* non-blocking */ }
  }, []);
  const inviteLink = (token) => `${publicBase}/therapist-intake/${token}`;
  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(inviteLink(token)); toast.success('Link copied'); }
    catch { toast.error('Could not copy — long-press to copy manually'); }
  };
  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const wantEmail = invSendEmail && !!invEmail.trim();
      const r = await http.post('/admin/therapists/invite', {
        name: invName.trim(), email: invEmail.trim(),
        expires_in_days: Number(invDays) || 14,
        send_email: wantEmail, origin: publicBase,
      });
      setInvName(''); setInvEmail('');
      await loadInvites();
      if (r.data.emailed) {
        toast.success(`Invite emailed to ${r.data.email}`);
      } else {
        await copyLink(r.data.token);
        toast.success(wantEmail ? 'Invite created & link copied (email could not be sent)' : 'Invite created — link copied. Send it to the therapist.');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create invite'); }
    finally { setCreatingInvite(false); }
  };
  const revokeInvite = async (token) => {
    if (!window.confirm('Revoke this invite link? The therapist will no longer be able to use it.')) return;
    try { await http.post(`/admin/therapists/invites/${token}/revoke`); loadInvites(); }
    catch { toast.error('Could not revoke'); }
  };
  const openInvites = () => { setShowInvites(true); loadInvites(); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await http.get('/admin/content/therapists'); setItems(Array.isArray(r.data) ? r.data : []); }
    catch { toast.error('Could not load therapists'); }
    finally { setLoading(false); }
  }, []);
  const loadTaxonomy = useCallback(async () => {
    try { const r = await http.get('/therapists/taxonomy'); setTaxonomy(r.data || { specialties: [], insurance: [], languages: [] }); }
    catch { /* non-blocking */ }
  }, []);
  const loadTrialStats = useCallback(async () => {
    try { const r = await http.get('/admin/therapists/trial-stats'); setTrialStats(r.data || null); }
    catch { /* non-blocking */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTaxonomy(); }, [loadTaxonomy]);
  useEffect(() => { loadTrialStats(); }, [loadTrialStats]);

  // Suggestions come from the master taxonomy (canonical + custom), so admin
  // selections always match the values that power the public directory filters.
  const suggest = useMemo(() => ({
    specialties: (taxonomy.specialties || []).map((x) => x.value),
    insurance: (taxonomy.insurance || []).map((x) => x.value),
    languages: (taxonomy.languages || []).map((x) => x.value),
  }), [taxonomy]);

  // Custom (non-canonical) terms currently in use — flagged for admin review,
  // never auto-overwritten.
  const customTerms = useMemo(() => {
    const g = (k) => (taxonomy[k] || []).filter((x) => x.source === 'custom' && x.count > 0);
    return { specialties: g('specialties'), insurance: g('insurance'), languages: g('languages') };
  }, [taxonomy]);
  const customCount = customTerms.specialties.length + customTerms.insurance.length + customTerms.languages.length;

  const openNew = () => setForm({ ...EMPTY });
  const openEdit = (t) => setForm({
    ...EMPTY, ...t,
    credentials: t.credentials || '', photo: t.photo || '', bio: t.bio || '',
    email: t.email || '', phone: t.phone || '',
    specialties: asArr(t.specialties), states: asArr(t.states && t.states.length ? t.states : (t.state ? [t.state] : [])),
    insurance: asArr(t.insurance), languages: asArr(t.languages),
    modality: t.modality || (t.telehealth ? 'telehealth' : 'in-person'),
    booking_url: t.booking_url || t.website || '',
  });

  const pickPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) { toast.error('Use JPG, PNG, or WEBP.'); return; }
    if (f.size > 2_200_000) { toast.error('Image must be under ~2MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, photo: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      const states = form.states;
      const payload = {
        ...(form.id ? { id: form.id, created_at: form.created_at } : {}),
        name: form.name.trim(), credentials: form.credentials.trim(), photo: form.photo || null,
        bio: form.bio.trim(), modality: form.modality,
        telehealth: form.modality === 'telehealth' || form.modality === 'both',
        specialties: form.specialties, states, state: states[0] || null,
        insurance: form.insurance, languages: form.languages,
        booking_url: form.booking_url.trim() || null, website: form.booking_url.trim() || null,
        email: form.email.trim() || null, phone: form.phone.trim() || null,
        faith_informed: !!form.faith_informed, trauma_informed: !!form.trauma_informed,
        published: !!form.published,
      };
      await http.post('/admin/content/therapists', payload);
      toast.success('Saved'); setForm(null); load(); loadTaxonomy();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete ${t.name}?`)) return;
    try {
      await http.delete(`/admin/content/therapists/${t.id}`);
      setItems((p) => p.filter((x) => x.id !== t.id));
      setSelected((s) => { const n = new Set(s); n.delete(t.id); return n; });
    }
    catch { toast.error('Delete failed'); }
  };
  const togglePublish = async (t) => {
    try { const r = await http.post(`/admin/content/therapists/${t.id}/publish`); setItems((p) => p.map((x) => x.id === t.id ? { ...x, published: r.data.published } : x)); loadTrialStats(); }
    catch { toast.error('Update failed'); }
  };
  const sendTrial = async (t) => {
    if (!t.email) { toast.error('Add an email to this therapist first, then send the trial.'); return; }
    if (!window.confirm(`Send a 7-day free trial invite to ${t.name || 'this therapist'} (${t.email})?`)) return;
    setTrialBusy(t.id);
    try {
      const r = await http.post(`/admin/therapists/${t.id}/send-trial`);
      toast.success(`7-day trial invite sent to ${r.data.email}`);
      loadTrialStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the trial'); }
    finally { setTrialBusy(null); }
  };
  const approveEdit = async (t) => {
    setEditBusy(true);
    try { await http.post(`/admin/therapists/${t.id}/edit/approve`); toast.success('Edit approved — now live'); setEditReview(null); await load(); loadTaxonomy(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not approve edit'); }
    finally { setEditBusy(false); }
  };
  const rejectEdit = async (t) => {
    setEditBusy(true);
    try { await http.post(`/admin/therapists/${t.id}/edit/reject`); toast.success('Edit discarded — listing unchanged'); setEditReview(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reject edit'); }
    finally { setEditBusy(false); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((t) => t.id)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const r = await http.post('/admin/content/therapists/bulk-delete', { ids });
      toast.success(`Deleted ${r.data.deleted} therapist${r.data.deleted === 1 ? '' : 's'}`);
      setConfirmBulk(false); clearSel(); await load(); loadTaxonomy();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk delete failed'); }
    finally { setBulkDeleting(false); }
  };

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'therapists_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setImporting(true);
      try {
        const r = await http.post('/admin/content/therapists/bulk-import', { csv: String(reader.result) });
        const errs = r.data.errors || [];
        toast.success(`Imported ${r.data.created} therapist${r.data.created === 1 ? '' : 's'}${errs.length ? ` · ${errs.length} row(s) skipped` : ''}`);
        if (errs.length) console.warn('CSV import skipped rows:', errs);
        await load(); loadTaxonomy();
      } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
      finally { setImporting(false); }
    };
    reader.readAsText(f);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-therapists-page">
      <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy" data-testid="back-to-admin"><ArrowLeft size={14}/> Back to Admin</Link>
      <div className="flex items-center gap-3">
        <Stethoscope className="text-gold" size={22}/>
        <div className="flex-1">
          <h1 className="font-heading text-3xl">Therapist Directory</h1>
          <p className="text-sm text-navy/55">Add and manage the licensed providers shown to members.</p>
        </div>
        <button onClick={downloadTemplate} data-testid="therapist-csv-template" className="rounded-full px-3 py-2 text-sm text-navy/70 hover:text-navy border border-navy/15 inline-flex items-center gap-2"><FileDown size={15}/> Template</button>
        <label data-testid="therapist-csv-import" className="cursor-pointer bg-beige text-navy rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:brightness-95">
          {importing ? <Loader2 size={15} className="animate-spin"/> : <Upload size={15}/>} {importing ? 'Importing…' : 'Import CSV'}
          <input type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" disabled={importing}/>
        </label>
        <button onClick={openInvites} data-testid="therapist-invite-open" className="bg-beige text-navy rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:brightness-95"><Link2 size={15}/> Invite therapist</button>
        <button onClick={openNew} data-testid="therapist-add" className="bg-navy text-white rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-navy-soft"><Plus size={15}/> Add therapist</button>
      </div>

      {trialStats && trialStats.sent > 0 && (
        <div data-testid="trial-stats">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} className="text-gold"/>
            <span className="text-xs uppercase tracking-widest text-navy/50 font-semibold">7-day trial funnel</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'sent', label: 'Invites sent', value: trialStats.sent, cls: 'text-navy', sub: `${trialStats.redemption_rate}% redeemed` },
              { key: 'redeemed', label: 'Redeemed', value: trialStats.redeemed, cls: 'text-gold', sub: trialStats.pending ? `${trialStats.pending} awaiting` : 'all claimed' },
              { key: 'active', label: 'Active now', value: trialStats.active, cls: 'text-green-600', sub: 'in trial' },
              { key: 'expired', label: 'Expired', value: trialStats.expired, cls: 'text-navy/50', sub: 'trial ended' },
            ].map((s) => (
              <div key={s.key} className="bg-white rounded-2xl p-4 card-soft" data-testid={`trial-stat-${s.key}`}>
                <p className={`font-heading text-3xl leading-none ${s.cls}`} data-testid={`trial-stat-${s.key}-value`}>{s.value}</p>
                <p className="text-sm text-navy/70 mt-1.5">{s.label}</p>
                <p className="text-[11px] text-navy/40 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      )}

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="therapist-review-panel">
          <button onClick={() => setShowReview((s) => !s)} className="w-full flex items-center gap-2 text-left" data-testid="therapist-review-toggle">
            <AlertTriangle size={16} className="text-amber-600"/>
            <span className="text-sm font-medium text-navy">{customCount} custom term{customCount === 1 ? '' : 's'} in use — review for consistency</span>
            <span className="ml-auto text-xs text-navy/50">{showReview ? 'Hide' : 'Show'}</span>
          </button>
          {showReview && (
            <div className="mt-3 space-y-2">
              {['specialties', 'insurance', 'languages'].map((k) => customTerms[k].length > 0 && (
                <div key={k}>
                  <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold">{k}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {customTerms[k].map((x) => (
                      <span key={x.value} className="text-[11px] bg-white border border-amber-200 text-navy/75 rounded-full px-2.5 py-1">{x.value} · {x.count}</span>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-navy/50 pt-1">These aren&rsquo;t on the canonical list. Edit the affected profiles to a standard term if they&rsquo;re duplicates — existing data is never changed automatically.</p>
            </div>
          )}
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white rounded-2xl px-4 py-3 card-soft flex items-center gap-3" data-testid="therapist-bulk-toolbar">
          <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-navy w-4 h-4" data-testid="therapist-select-all"/>
            {allSelected ? 'Deselect all' : 'Select all'}
          </label>
          {selected.size > 0 && (
            <>
              <span className="text-sm text-navy/55" data-testid="therapist-selected-count">{selected.size} selected</span>
              <button onClick={clearSel} className="text-xs text-navy/50 hover:text-navy">Clear</button>
              <button onClick={() => setConfirmBulk(true)} data-testid="therapist-bulk-delete" className="ml-auto inline-flex items-center gap-2 bg-red-600 text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-red-700"><Trash2 size={14}/> Delete selected</button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-navy/45"><Loader2 className="inline animate-spin" size={20}/></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-navy/50 card-soft">No therapists yet. Click &ldquo;Add therapist&rdquo;.</div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id} className={`bg-white rounded-2xl p-4 card-soft flex items-center gap-3 ${selected.has(t.id) ? 'ring-2 ring-navy/30' : ''}`} data-testid={`therapist-row-${t.id}`}>
              <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} className="accent-navy w-4 h-4 shrink-0" data-testid={`therapist-select-${t.id}`}/>
              {t.photo ? <img src={t.photo} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-gold/40"/> : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-beige to-gold/45 text-navy flex items-center justify-center font-heading text-sm">{(t.name || 'T').slice(0, 1)}</div>}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{t.name} <span className="text-navy/40 font-normal">{t.credentials}</span></p>
                <p className="text-xs text-navy/50 truncate">{asArr(t.states && t.states.length ? t.states : (t.state ? [t.state] : [])).join(', ')} · {asArr(t.specialties).slice(0, 3).join(', ')}</p>
              </div>
              {!t.published && <span className="text-[10px] uppercase tracking-widest bg-beige text-navy/60 rounded-full px-2 py-1">Draft</span>}
              {t.source === 'self_service' && !t.published && <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 rounded-full px-2 py-1" data-testid={`therapist-pending-${t.id}`}>Pending review</span>}
              {t.pending_edit && <button onClick={() => setEditReview(t)} data-testid={`therapist-edit-review-${t.id}`} className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 rounded-full px-2.5 py-1 hover:bg-amber-200 inline-flex items-center gap-1"><Clock size={11}/> Edit pending · Review</button>}
              <button onClick={() => togglePublish(t)} title="Publish/unpublish" className="p-2 text-navy/50 hover:text-navy" data-testid={`therapist-publish-${t.id}`}>{t.published ? <Eye size={16}/> : <EyeOff size={16}/>}</button>
              {t.manage_token && (
                <button onClick={() => { navigator.clipboard.writeText(`${publicBase}/therapist-manage/${t.manage_token}`).then(() => toast.success('Edit link copied — send it to the therapist')).catch(() => toast.error('Could not copy')); }}
                  title="Copy the therapist's private edit link" className="p-2 text-navy/50 hover:text-navy" data-testid={`therapist-editlink-${t.id}`}><Link2 size={16}/></button>
              )}
              <button onClick={() => sendTrial(t)} disabled={trialBusy === t.id} title="Send a 7-day free trial invite" className="p-2 text-navy/50 hover:text-gold disabled:opacity-40" data-testid={`therapist-send-trial-${t.id}`}>{trialBusy === t.id ? <Loader2 size={16} className="animate-spin"/> : <Gift size={16}/>}</button>
              <button onClick={() => openEdit(t)} className="text-xs px-3 py-1.5 rounded-full bg-beige text-navy hover:brightness-95" data-testid={`therapist-edit-${t.id}`}>Edit</button>
              <button onClick={() => del(t)} className="p-2 text-red-500 hover:bg-red-50 rounded-full" data-testid={`therapist-delete-${t.id}`}><Trash2 size={15}/></button>
            </div>
          ))}
        </div>
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-[75] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !bulkDeleting && setConfirmBulk(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="therapist-bulk-confirm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center"><Trash2 size={18} className="text-red-600"/></div>
              <h3 className="font-heading text-lg">Delete {selected.size} therapist{selected.size === 1 ? '' : 's'}?</h3>
            </div>
            <p className="text-sm text-navy/60 mt-3">This permanently removes the selected profile{selected.size === 1 ? '' : 's'} from the directory. This can&rsquo;t be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmBulk(false)} disabled={bulkDeleting} className="px-4 py-2 rounded-lg text-sm text-navy/60 hover:bg-navy/5">Cancel</button>
              <button onClick={bulkDelete} disabled={bulkDeleting} data-testid="therapist-bulk-confirm-btn" className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">{bulkDeleting && <Loader2 size={14} className="animate-spin"/>} Delete {selected.size}</button>
            </div>
          </div>
        </div>
      )}

      {showInvites && (
        <div className="fixed inset-0 z-[72] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowInvites(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="therapist-invite-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-xl inline-flex items-center gap-2"><Link2 size={18} className="text-gold"/> Invite a therapist</h3>
              <button onClick={() => setShowInvites(false)}><X size={18} className="text-navy/40"/></button>
            </div>
            <p className="text-sm text-navy/55 mb-4">Generate a private link. The therapist fills in their own profile and headshot; it arrives here as <span className="font-medium">Pending review</span> for you to approve.</p>

            <div className="bg-navy text-white rounded-2xl p-4 mb-4" data-testid="apply-link-block">
              <p className="text-xs uppercase tracking-widest text-gold font-semibold">Shareable apply link</p>
              <p className="text-xs text-white/60 mt-1">One link you can post or share with anyone. Every applicant lands here as “Pending review” for your approval.</p>
              <div className="mt-3 flex items-center gap-2">
                <input readOnly value={`${publicBase}/therapist-apply`} data-testid="apply-link-input"
                  className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-xs text-white/90 truncate focus:outline-none"/>
                <button onClick={() => { navigator.clipboard.writeText(`${publicBase}/therapist-apply`).then(() => toast.success('Apply link copied')).catch(() => toast.error('Could not copy')); }}
                  data-testid="apply-link-copy" className="bg-gold text-navy rounded-lg px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5"><Copy size={13}/> Copy</button>
              </div>
            </div>

            <p className="text-xs uppercase tracking-widest text-navy/40 font-semibold mb-2">Or invite one therapist</p>
            <div className="bg-[#F9F8F6] rounded-2xl p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={invName} onChange={(e) => setInvName(e.target.value)} placeholder="Therapist name (optional)" data-testid="invite-name" className="bg-white border border-navy/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"/>
                <input value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="Email (optional)" data-testid="invite-email" className="bg-white border border-navy/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"/>
              </div>
              <div className="flex items-center gap-2 text-sm text-navy/60">
                <span>Link expires in</span>
                <select value={invDays} onChange={(e) => setInvDays(e.target.value)} data-testid="invite-days" className="bg-white border border-navy/10 rounded-lg px-2 py-1 text-sm">
                  <option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
                </select>
                <button onClick={createInvite} disabled={creatingInvite} data-testid="invite-create" className="ml-auto bg-navy text-white rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                  {creatingInvite ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} {invSendEmail && invEmail.trim() ? 'Create & email' : 'Create link'}
                </button>
              </div>
              <label className={`flex items-center gap-2 text-sm ${invEmail.trim() ? 'text-navy/70' : 'text-navy/35'}`}>
                <input type="checkbox" checked={invSendEmail} disabled={!invEmail.trim()} onChange={(e) => setInvSendEmail(e.target.checked)} data-testid="invite-send-email" className="accent-navy"/>
                Email this link straight to the therapist{!invEmail.trim() && ' (add an email above)'}
              </label>
            </div>

            <div className="mt-4 space-y-2 max-h-[45vh] overflow-y-auto" data-testid="invite-list">
              {invites.length === 0 ? (
                <p className="text-sm text-navy/45 text-center py-6">No invites yet.</p>
              ) : invites.map((iv) => {
                const expired = iv.expires_at && iv.expires_at < new Date().toISOString();
                const status = iv.status === 'completed' ? 'Completed' : iv.status === 'revoked' ? 'Revoked' : expired ? 'Expired' : 'Active';
                const tone = status === 'Active' ? 'bg-green/10 text-green' : status === 'Completed' ? 'bg-navy/10 text-navy/70' : 'bg-beige text-navy/50';
                return (
                  <div key={iv.token} className="bg-white border border-navy/10 rounded-xl px-3 py-2.5 flex items-center gap-2" data-testid={`invite-row-${iv.token}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{iv.name || 'Unnamed'} <span className="text-navy/40 font-normal">{iv.email}</span></p>
                      <p className="text-[11px] text-navy/45 inline-flex items-center gap-1"><Clock size={11}/> {status === 'Active' ? `Expires ${new Date(iv.expires_at).toLocaleDateString()}` : status}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-1 ${tone}`}>{status}</span>
                    {iv.status === 'pending' && !expired && (
                      <>
                        <button onClick={() => copyLink(iv.token)} title="Copy link" data-testid={`invite-copy-${iv.token}`} className="p-2 text-navy/60 hover:text-navy"><Copy size={15}/></button>
                        <button onClick={() => revokeInvite(iv.token)} title="Revoke" data-testid={`invite-revoke-${iv.token}`} className="p-2 text-red-500 hover:bg-red-50 rounded-full"><Trash2 size={15}/></button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="therapist-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl">{form.id ? 'Edit' : 'New'} therapist</h3>
              <button onClick={() => setForm(null)}><X size={18} className="text-navy/40"/></button>
            </div>
            <div className="space-y-3">
              {/* Headshot */}
              <div>
                <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Headshot</label>
                <p className="text-[11px] text-navy/45 mt-0.5">A friendly, real photo helps members feel at ease. Square images look best.</p>
                <div className="mt-2 flex items-center gap-3">
                  {form.photo ? <img src={form.photo} alt="preview" className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/40"/> : <div className="w-16 h-16 rounded-full bg-gradient-to-br from-beige to-gold/45 flex items-center justify-center text-navy/40"><Upload size={18}/></div>}
                  <div className="flex-1">
                    <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden"/>
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="tf-photo-upload" className="bg-navy text-white rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-2"><Upload size={13}/> Upload picture</button>
                    {form.photo && <button type="button" onClick={() => setForm({ ...form, photo: '' })} className="ml-2 text-xs text-red-500">Remove</button>}
                    <input value={form.photo?.startsWith('data:') ? '' : form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} placeholder="…or paste an image URL" data-testid="tf-photo"
                      className="mt-2 w-full bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-gold"/>
                  </div>
                </div>
              </div>
              <Field label="Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} ph="Dr. Marcus Bell" testid="tf-name"/>
              <Field label="Credentials" v={form.credentials} on={(v) => setForm({ ...form, credentials: v })} ph="LCSW, PhD" testid="tf-credentials"/>
              <Field label="Bio" v={form.bio} on={(v) => setForm({ ...form, bio: v })} area testid="tf-bio"/>
              <div>
                <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Modality</label>
                <select value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })} data-testid="tf-modality" className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-xl px-3 py-2 text-sm">
                  <option value="telehealth">Telehealth</option>
                  <option value="in-person">In-person</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <TagInput label="Specialties" values={form.specialties} onChange={(v) => setForm({ ...form, specialties: v })} suggestions={suggest.specialties} testid="tf-specialties"/>
              <TagInput label="States licensed" values={form.states} onChange={(v) => setForm({ ...form, states: v })} suggestions={US_STATES} testid="tf-states" fixed/>
              <TagInput label="Insurance accepted" values={form.insurance} onChange={(v) => setForm({ ...form, insurance: v })} suggestions={suggest.insurance} testid="tf-insurance"/>
              <TagInput label="Languages" values={form.languages} onChange={(v) => setForm({ ...form, languages: v })} suggestions={suggest.languages} testid="tf-languages"/>
              <Field label="Booking URL / Website" v={form.booking_url} on={(v) => setForm({ ...form, booking_url: v })} ph="https://…" testid="tf-booking"/>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} ph="hi@…" testid="tf-email"/>
                <Field label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} ph="(555) …" testid="tf-phone"/>
              </div>
              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.faith_informed} onChange={(e) => setForm({ ...form, faith_informed: e.target.checked })} className="accent-navy"/> Faith-informed</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.trauma_informed} onChange={(e) => setForm({ ...form, trauma_informed: e.target.checked })} className="accent-navy"/> Trauma-informed</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-navy" data-testid="tf-published"/> Published</label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg text-sm text-navy/60 hover:bg-navy/5">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="therapist-save" className="px-5 py-2 rounded-lg bg-navy text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">{saving && <Loader2 size={14} className="animate-spin"/>} Save</button>
            </div>
          </div>
        </div>
      )}

      {editReview && (() => {
        const pe = editReview.pending_edit || {};
        const FIELDS = [
          ['name', 'Name'], ['credentials', 'Credentials'], ['bio', 'Bio'], ['modality', 'Modality'],
          ['specialties', 'Specialties'], ['states', 'States'], ['insurance', 'Insurance'], ['languages', 'Languages'],
          ['booking_url', 'Booking / Website'], ['email', 'Email'], ['phone', 'Phone'],
          ['faith_informed', 'Faith-informed'], ['trauma_informed', 'Trauma-informed'], ['photo', 'Headshot'],
        ];
        const norm = (v) => Array.isArray(v) ? [...v] : (v ?? '');
        const fmt = (k, v) => {
          if (k === 'photo') return v ? 'Photo set' : '—';
          if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
          if (typeof v === 'boolean') return v ? 'Yes' : 'No';
          return v || '—';
        };
        const changed = FIELDS.filter(([k]) => JSON.stringify(norm(editReview[k])) !== JSON.stringify(norm(pe[k])));
        return (
          <div className="fixed inset-0 z-[76] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => !editBusy && setEditReview(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="therapist-edit-review-modal">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading text-xl inline-flex items-center gap-2"><Clock size={18} className="text-amber-600"/> Review requested edit</h3>
                <button onClick={() => setEditReview(null)}><X size={18} className="text-navy/40"/></button>
              </div>
              <p className="text-sm text-navy/55 mb-4"><span className="font-medium text-navy">{editReview.name}</span> submitted changes. Approve to make them live, or reject to keep the current listing.</p>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {changed.length === 0 ? (
                  <p className="text-sm text-navy/45 text-center py-4">No differences from the live profile.</p>
                ) : changed.map(([k, label]) => (
                  <div key={k} className="bg-[#F9F8F6] rounded-xl p-3" data-testid={`edit-diff-${k}`}>
                    <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold mb-1">{label}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div className="text-navy/50 line-through decoration-red-300">{fmt(k, editReview[k])}</div>
                      <div className="text-navy font-medium">{fmt(k, pe[k])}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => rejectEdit(editReview)} disabled={editBusy} data-testid="edit-reject-btn" className="px-4 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 disabled:opacity-40 inline-flex items-center gap-2">{editBusy && <Loader2 size={14} className="animate-spin"/>} Reject</button>
                <button onClick={() => approveEdit(editReview)} disabled={editBusy} data-testid="edit-approve-btn" className="px-5 py-2 rounded-lg bg-navy text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">{editBusy && <Loader2 size={14} className="animate-spin"/>} Approve &amp; publish</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
